import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, Download, Pencil, Check, X } from "lucide-react";
import { downloadUtensilChecklist } from "@/lib/pdf";

function fmtWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-MY", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/** Per-order catering utensil checklist with created-by / checked-by audit trail. */
export default function UtensilChecklist({ orderId }) {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [qty, setQty] = useState(1);
  const [editId, setEditId] = useState(null);       // id of the row being edited
  const [editLabel, setEditLabel] = useState("");
  const [editQty, setEditQty] = useState(1);

  const load = useCallback(() => {
    api.get(`/orders/${orderId}/utensils`).then((r) => setItems(r.data || [])).catch(() => {});
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    try {
      await api.post(`/orders/${orderId}/utensils`, { label: label.trim(), qty: Number(qty) || 1 });
      setLabel(""); setQty(1);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/orders/${orderId}/utensils/${it.id}`, { checked: !it.checked });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (it) => {
    try {
      await api.delete(`/orders/${orderId}/utensils/${it.id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const startEdit = (it) => { setEditId(it.id); setEditLabel(it.label); setEditQty(it.qty); };
  const cancelEdit = () => { setEditId(null); setEditLabel(""); setEditQty(1); };
  const saveEdit = async (it) => {
    if (!editLabel.trim()) { toast.error("Name is required"); return; }
    try {
      await api.patch(`/orders/${orderId}/utensils/${it.id}`, { label: editLabel.trim(), qty: Number(editQty) || 1 });
      toast.success("Utensil updated");
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const done = items.filter((i) => i.checked).length;

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/orders/${orderId}`);
      downloadUtensilChecklist(r.data, items);
    } catch {
      toast.error("Could not generate PDF");
    }
  };

  return (
    <div className="card-lux p-6" data-testid="utensil-checklist">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> Utensils</div>
          <h3 className="font-display text-xl mt-1">Catering utensil checklist</h3>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && <div className="text-[#D4AF37] font-display text-xl">{done}/{items.length}</div>}
          <button onClick={downloadPdf} data-testid="utensil-pdf-btn" title="Download checklist PDF"
            className="p-2 rounded-sm border border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      <form onSubmit={add} className="flex gap-2 mb-3">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Chafing dish / Serving tongs / Ladle" data-testid="utensil-label-input" className="flex-1 bg-[#050505] border-[#2a2a2a] h-9" />
        <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="utensil-qty-input" className="w-20 bg-[#050505] border-[#2a2a2a] h-9" />
        <Button type="submit" disabled={!label.trim()} data-testid="utensil-add-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] h-9 px-3">
          <Plus className="w-4 h-4" />
        </Button>
      </form>

      {items.length === 0 ? (
        <div className="text-xs text-zinc-600 py-3 text-center">No utensils listed yet — add what this event needs.</div>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
          {items.map((it) => (
            <div key={it.id} className="flex items-start gap-3 p-2 rounded-sm hover:bg-[#0c0c0c] group" data-testid={`utensil-row-${it.id}`}>
              <input type="checkbox" checked={it.checked} onChange={() => toggle(it)} data-testid={`utensil-check-${it.id}`} className="accent-[#D4AF37] w-4 h-4 mt-0.5 cursor-pointer" />
              {it.image_data_url && (
                <img src={it.image_data_url} alt={it.label} className="w-9 h-9 rounded-sm object-contain bg-[#101010] border border-[#1a1a1a] shrink-0" />
              )}
              {editId === it.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} data-testid={`utensil-edit-label-${it.id}`} className="flex-1 h-8 bg-[#050505] border-[#2a2a2a]" />
                  <Input type="number" min="1" value={editQty} onChange={(e) => setEditQty(e.target.value)} data-testid={`utensil-edit-qty-${it.id}`} className="w-16 h-8 bg-[#050505] border-[#2a2a2a]" />
                  <button onClick={() => saveEdit(it)} data-testid={`utensil-edit-save-${it.id}`} title="Save" className="p-1 text-emerald-500 hover:text-emerald-400"><Check className="w-4 h-4" /></button>
                  <button onClick={cancelEdit} data-testid={`utensil-edit-cancel-${it.id}`} title="Cancel" className="p-1 text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${it.checked ? "line-through text-zinc-600" : "text-zinc-200"}`}>
                      {it.label} <span className="text-[#D4AF37]">×{it.qty} {it.uom || ""}</span>
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">
                      Added by {it.created_by_name || "—"}
                      {it.checked && it.checked_by_name && (
                        <span className="text-emerald-500"> · ✓ checked by {it.checked_by_name} · {fmtWhen(it.checked_at)}</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => startEdit(it)} data-testid={`utensil-edit-${it.id}`} title="Edit" className="p-1 text-zinc-600 hover:text-[#D4AF37] md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(it)} data-testid={`utensil-delete-${it.id}`} title="Remove" className="p-1 text-zinc-600 hover:text-red-400 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
